// src/pages/Admin/Vote/VoteQuestionsModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  getVoteById,
  addQuestionToVote,
  updateQuestion,
  deleteQuestion,
  addChoiceToQuestion,
  deleteChoice,
} from "../../../services/voteServices";
import { toast } from "react-toastify";
import Button from "../../../components/Button/Button";
import ButtonEdit from "../../../components/Button/ButtonEdit";
import ButtonDelete from "../../../components/Button/ButtonDelete";
import { FaSave, FaTimes, FaQuestionCircle, FaListUl } from "react-icons/fa";

// Barre d’outils Quill
const modules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    ["link", "image", "video"],
    ["clean"],
  ],
};
const formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "color",
  "background",
  "script",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
];

// Helpers
const isEmptyHtml = (html) => {
  if (!html) return true;
  // supprime les balises & espaces insécables
  const text = html
    .replace(/<(.|\n)*?>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();
  return text.length === 0;
};

export default function VoteQuestionsModal({
  isOpen,
  onClose,
  voteId,
  voteTitle,
}) {
  const [questions, setQuestions] = useState([]);
  const [form, setForm] = useState({ question_text: "", type: "single" });
  const [editingId, setEditingId] = useState(null);

  const [choiceForms, setChoiceForms] = useState({}); // { [questionId]: "texte" }
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingChoice, setSavingChoice] = useState(false);

  // Reset à l’ouverture
  useEffect(() => {
    if (!isOpen || !voteId) return;
    setForm({ question_text: "", type: "single" });
    setEditingId(null);
    setChoiceForms({});
    fetchQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, voteId]);

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const vote = await getVoteById(voteId);
      setQuestions(Array.isArray(vote?.questions) ? vote.questions : []);
    } catch (error) {
      toast.error("Erreur lors du chargement des questions");
    } finally {
      setLoading(false);
    }
  };

  const handleQuillChange = (value) => {
    setForm((prev) => ({ ...prev, question_text: value }));
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const canSubmit = useMemo(() => {
    return (
      !saving &&
      !isEmptyHtml(form.question_text) &&
      ["single", "multiple", "open"].includes(form.type)
    );
  }, [form, saving]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.warn("Veuillez saisir une question.");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await updateQuestion(editingId, {
          question_text: form.question_text,
          type: form.type,
        });
        toast.success("Question modifiée");
      } else {
        await addQuestionToVote(voteId, {
          question_text: form.question_text,
          type: form.type,
        });
        toast.success("Question ajoutée");
      }
      setForm({ question_text: "", type: "single" });
      setEditingId(null);
      await fetchQuestions();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (q) => {
    setForm({ question_text: q.question_text || "", type: q.type || "single" });
    setEditingId(q.id);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette question ?")) return;
    try {
      await deleteQuestion(id);
      toast.success("Question supprimée");
      await fetchQuestions();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleChoiceChange = (questionId, value) => {
    setChoiceForms((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleAddChoice = async (questionId) => {
    const text = (choiceForms[questionId] || "").trim();
    if (!text || savingChoice) return;
    setSavingChoice(true);
    try {
      await addChoiceToQuestion(questionId, { choice_text: text });
      toast.success("Réponse ajoutée");
      setChoiceForms((prev) => ({ ...prev, [questionId]: "" }));
      await fetchQuestions();
    } catch (err) {
      toast.error("Erreur ajout réponse");
    } finally {
      setSavingChoice(false);
    }
  };

  const handleDeleteChoice = async (choiceId) => {
    if (!window.confirm("Supprimer cette réponse ?")) return;
    try {
      await deleteChoice(choiceId);
      toast.success("Réponse supprimée");
      await fetchQuestions();
    } catch (err) {
      toast.error("Erreur suppression réponse");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay cliquable */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      {/* Modal */}
      <div className="relative w-full max-w-5xl overflow-hidden rounded-2xl shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200/70 dark:ring-slate-700/70 max-h-[90vh] flex flex-col">
        {/* Header dégradé */}
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
          <h3 className="text-white text-lg sm:text-xl font-bold text-center uppercase">
            Questions du vote :{" "}
            <span className="italic font-light">{voteTitle}</span>
          </h3>
        </div>

        {/* Contenu scrollable */}
        <div className="p-5 sm:p-6 space-y-5 overflow-y-auto">
          {/* Formulaire question */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/50">
            <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center gap-2">
              <FaQuestionCircle className="text-slate-600 dark:text-slate-300" />
              <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                {editingId ? "Modifier la question" : "Ajouter une question"}
              </span>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-3">
              <div className="min-h-[8rem]">
                <ReactQuill
                  key={`${isOpen}-${editingId}`} // force le reset propre
                  value={form.question_text}
                  onChange={handleQuillChange}
                  theme="snow"
                  placeholder="Écrire la question ici (texte, image, mise en forme)…"
                  modules={modules}
                  formats={formats}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Type de question
                  </label>
                  <select
                    name="type"
                    value={form.type}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  >
                    <option value="single">Réponse unique</option>
                    <option value="multiple">Réponses multiples</option>
                    <option value="open">Réponse libre</option>
                  </select>
                </div>

                <div className="flex items-end gap-2 justify-end">
                  <Button
                    text="Annuler"
                    icon={FaTimes}
                    type="button"
                    onClick={() => {
                      setForm({ question_text: "", type: "single" });
                      setEditingId(null);
                    }}
                    className="!bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 !rounded-xl gap-2"
                  />
                  <Button
                    text={editingId ? "Modifier" : "Ajouter"}
                    icon={FaSave}
                    type="submit"
                    disabled={!canSubmit}
                    className={`!rounded-xl gap-2 ${
                      !canSubmit ? "opacity-60 pointer-events-none" : ""
                    }`}
                  />
                </div>
              </div>

              {isEmptyHtml(form.question_text) && (
                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                  Le texte de la question est requis.
                </p>
              )}
            </form>
          </div>

          {/* Liste des questions */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/50">
            <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center gap-2">
              <FaListUl className="text-slate-600 dark:text-slate-300" />
              <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                Questions enregistrées
              </span>
            </div>

            {loading ? (
              <div className="p-4 space-y-2">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="h-16 rounded-lg border border-slate-200 dark:border-slate-800 bg-white/60 dark:bg-slate-900/40 animate-pulse"
                  />
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50/80 dark:bg-slate-900/80 uppercase tracking-wide text-xs">
                      <th className="px-3 py-2 text-left font-semibold text-slate-700 dark:text-white">
                        Question
                      </th>
                      <th className="px-3 py-2 text-left font-semibold text-slate-700 dark:text-white w-40">
                        Type
                      </th>
                      <th className="px-3 py-2 text-left font-semibold text-slate-700 dark:text-white w-40">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {questions.length > 0 ? (
                      questions.map((q) => (
                        <React.Fragment key={q.id}>
                          <tr className="align-top hover:bg-slate-50/70 dark:hover:bg-slate-800/40">
                            <td className="px-3 py-3">
                              <div
                                className="prose prose-sm max-w-none dark:prose-invert"
                                dangerouslySetInnerHTML={{
                                  __html: q.question_text,
                                }}
                              />
                            </td>
                            <td className="px-3 py-3 capitalize text-slate-700 dark:text-slate-300">
                              {q.type === "single"
                                ? "Réponse unique"
                                : q.type === "multiple"
                                ? "Réponses multiples"
                                : "Réponse libre"}
                            </td>
                            <td className="px-3 py-3">
                              <div className="flex items-center gap-2">
                                <ButtonEdit onClick={() => handleEdit(q)} />
                                <ButtonDelete
                                  onClick={() => handleDelete(q.id)}
                                />
                              </div>
                            </td>
                          </tr>

                          {(q.type === "single" || q.type === "multiple") && (
                            <>
                              {/* Choix existants */}
                              {q.choices?.map((choice) => (
                                <tr
                                  key={choice.id}
                                  className="bg-white/60 dark:bg-slate-900/40"
                                >
                                  <td className="px-3 py-2 pl-8" colSpan={2}>
                                    ⏹️ {choice.choice_text}
                                  </td>
                                  <td className="px-3 py-2">
                                    <div className="flex items-center gap-2">
                                      <ButtonDelete
                                        onClick={() =>
                                          handleDeleteChoice(choice.id)
                                        }
                                      />
                                    </div>
                                  </td>
                                </tr>
                              ))}

                              {/* Ajouter un choix */}
                              <tr className="bg-slate-50/60 dark:bg-slate-900/30">
                                <td className="px-3 py-3" colSpan={2}>
                                  <input
                                    type="text"
                                    value={choiceForms[q.id] || ""}
                                    onChange={(e) =>
                                      handleChoiceChange(q.id, e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        e.preventDefault();
                                        handleAddChoice(q.id);
                                      }
                                    }}
                                    placeholder="Ajouter un choix…"
                                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                                  />
                                </td>
                                <td className="px-3 py-3">
                                  <Button
                                    text={savingChoice ? "Ajout…" : "Ajouter"}
                                    onClick={() => handleAddChoice(q.id)}
                                    className={`btn-primary text-xs !rounded-xl ${
                                      savingChoice
                                        ? "opacity-60 pointer-events-none"
                                        : ""
                                    }`}
                                  />
                                </td>
                              </tr>
                            </>
                          )}
                        </React.Fragment>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={3}
                          className="px-3 py-8 text-center text-slate-600 dark:text-slate-300"
                        >
                          Aucune question enregistrée pour ce vote.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pied de modale */}
          <div className="flex justify-center">
            <Button
              text="Fermer"
              icon={FaTimes}
              onClick={onClose}
              className="gap-2 !rounded-xl"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
